import { createContext, useCallback, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Details open over the current page as `?karte=<versionId>`.
 *
 * The parameter names a card *version*, so a shared or bookmarked link shows
 * exactly what was read, and a direct load opens the details over whatever page
 * the path names. Opening pushes a history entry, so browser Back closes it.
 * Closing an entry we pushed goes back; closing a directly loaded link removes
 * the parameter in place, so Close never leaves the site.
 */

export const DETAIL_PARAM = 'karte';

/**
 * `?ausgabe=<briefingId>` names the edition a card was opened from. The details
 * then compare the version with the baseline stored on that edition's entry,
 * so a link into an old edition compares exactly what that edition compared.
 */
export const EDITION_PARAM = 'ausgabe';

/** The edition whose cards are on screen; null outside an edition. */
export const EditionContext = createContext<string | null>(null);

let lastOpener: HTMLElement | null = null;

/**
 * The element that opened the details, to receive focus again on close. Reading
 * does not clear it: React may mount an effect twice (StrictMode), and the
 * second mount must still find the opener. The next open replaces it.
 */
export function peekOpener(): HTMLElement | null {
  return lastOpener;
}

export function detailHref(pathname: string, search: string, versionId: string): string {
  const params = new URLSearchParams(search);
  params.set(DETAIL_PARAM, versionId);
  return `${pathname}?${params.toString()}`;
}

export function useOpenDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const edition = useContext(EditionContext);
  return useCallback(
    (versionId: string, opener?: HTMLElement | null) => {
      lastOpener = opener ?? (document.activeElement as HTMLElement | null);
      const params = new URLSearchParams(location.search);
      const alreadyOpen = params.has(DETAIL_PARAM);
      params.set(DETAIL_PARAM, versionId);
      // Opened from an edition: name it. Anywhere else, including switching to
      // another version inside open details, the edition no longer applies.
      if (edition) params.set(EDITION_PARAM, edition);
      else params.delete(EDITION_PARAM);
      navigate(
        { pathname: location.pathname, search: `?${params.toString()}` },
        // Switching versions inside open details replaces, so Back still closes.
        { state: { detailPushed: alreadyOpen ? (location.state as { detailPushed?: boolean } | null)?.detailPushed ?? false : true }, replace: alreadyOpen },
      );
    },
    [edition, location.pathname, location.search, location.state, navigate],
  );
}

export function useCloseDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(() => {
    const pushed = (location.state as { detailPushed?: boolean } | null)?.detailPushed;
    if (pushed) {
      navigate(-1);
      return;
    }
    const params = new URLSearchParams(location.search);
    params.delete(DETAIL_PARAM);
    params.delete(EDITION_PARAM);
    const qs = params.toString();
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
  }, [location.pathname, location.search, location.state, navigate]);
}
